import {EnhancedDetailsList} from '../EnhancedDetailsList';
import {ColumnKey} from '../EnhancedDetailsList/IEnhancedDetailsList';
import {Document} from '../../../models';
import {DocumentSystem} from '../../../models/DocumentSystem';
import {DocumentVersion} from '../../../models/DocumentVersion';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {sortByTitle} from '../../../helpers/arrayMehods';
import {useService} from '../../../hooks';
import {getColumnsAdd, getColumnsEdit, getColumnsView} from './documentAndFileColumns';
import {DocumentListUpdateModal} from './DocumentListUpdateModal';
import { mapDocumentToDocumentSystem, mapNewItemToChangedItem } from '../../../helpers/documentMapper';
import { useFavoritesStore } from '../../../store/useFavoriteStore';

function mapChangedDocumentToDocument(changedItem: DocumentSystem): DocumentSystem {
  return changedItem;
}

export const DocumentAndFile: FC<{
  data: DocumentSystem[] | null;
  changedDocuments?: DocumentSystem[];
  hiddenColumns?: ColumnKey[];
  allowAdd?: boolean;
  isLoadDocumentsBefore?: boolean;
  ownerProcessId?: number;
  documentsList?: Document[] | undefined;
  itemsFiltered? : boolean;
  pageSize?: number;
  pdcOperatingUnit?: string | null;
}> = ({data, changedDocuments, allowAdd,isLoadDocumentsBefore, documentsList, ownerProcessId, pageSize, pdcOperatingUnit,itemsFiltered}) => {
  const addedDocumentId = useFavoritesStore (state => state.addedDocumentId);
  const addedLanguageId = useFavoritesStore (state => state.addedLanguageId);
  const removeDocumentId = useFavoritesStore (state => state.removedDocumentId);
  const removeLanguageId = useFavoritesStore (state => state.removedLanguageId);
  const reset = useFavoritesStore((state) => state.reset);
  const {getDocuments} = useService();
  useEffect(() => {
    if (!allowAdd)
      {
      return;
      } 
    else if(documentsList && documentsList.length > 0){
      setDocuments(documentsList.sort(sortByTitle));
      return;
    }
    else if(!isLoadDocumentsBefore)
    (async () => {
      const documentsResponse = await getDocuments();
      if (!documentsResponse.result) return;
      setDocuments(documentsResponse.result.sort(sortByTitle));
    })();
  }, [getDocuments, allowAdd,documentsList,isLoadDocumentsBefore]);

  const [isAddModal, setIsAddModal] = useState(false);
  const [editItem, setEditItem] = useState<DocumentSystem | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsChangedCount, setDocumentsChangedCount] = useState(0);
  const [parents, setParents] = useState<DocumentSystem[] | null>(null);
  const changedItems = useMemo(() => changedDocuments || [], [changedDocuments]);


  const refreshDocuments = useMemo(
    () => async () => {
      const docsResponse = await getDocuments();
      reset();
      if (!docsResponse.result) return;
      setDocuments(docsResponse.result.sort(sortByTitle));
    },
    [getDocuments, setDocuments,reset]
  );

   useEffect(() => {
  if (!addedDocumentId || !addedLanguageId) return;

  setDocuments(prev =>
    prev.map(doc =>
      doc.documentId === addedDocumentId && doc.languageCode === addedLanguageId
        ? {
            ...doc,
            isFavorite: true
          }
        : doc
    )
  );
}, [addedDocumentId, addedLanguageId]);

 useEffect(() => {
  if (!removeDocumentId || !removeLanguageId) return;

  setDocuments(prev =>
    prev.map(doc =>
      doc.documentId === removeDocumentId && doc.languageCode === removeLanguageId
        ? {
            ...doc,
            isFavorite: false
          }
        : doc
    )
  );
}, [removeDocumentId, removeLanguageId]);

  const onOpen = useCallback(
    (item: DocumentSystem) => {
      let possibleParents: DocumentSystem[] = [];
      data?.forEach(x => possibleParents.push(x));

      changedDocuments?.forEach(x => {
        const possibleParent = possibleParents.find(y => y.id === x.id);
        if (possibleParent === undefined) {
          possibleParents.push(x);
        }
      });

      if (item.relevantToId !== null) {
        let currentParent = possibleParents.find(x => x.id === item.relevantToId);

        if (currentParent === undefined) {
          const document = documents?.find(x => x.id === item.relevantToId);

          if (document !== undefined) {
            currentParent = mapDocumentToDocumentSystem(document);
            possibleParents.push(currentParent);
          }
        }
      }

      setParents(possibleParents);
      setIsAddModal(true);
    },
    [changedDocuments, data, documents]
  );

  const onClose = useCallback(() => {
    setEditItem(null);
    setIsAddModal(false);
  }, []);

  const onEditClick = useCallback(
    (item: DocumentSystem) => {
      setEditItem(item);
      onOpen(item);
    },
    [onOpen]
  );

  const columnsView = useMemo(
    () => getColumnsView(changedDocuments, ownerProcessId),
    [changedDocuments, ownerProcessId]
  );
  const columnsEdit = useMemo(
    () => getColumnsEdit(changedDocuments, ownerProcessId, onEditClick),
    [changedDocuments, ownerProcessId, onEditClick]
  );
  const columnsAdd = useMemo(
    () => getColumnsAdd(changedDocuments, ownerProcessId, pdcOperatingUnit),
    [changedDocuments, ownerProcessId, pdcOperatingUnit]
  );

  const updateDocuments = useCallback(
    (modifiedDoc: DocumentVersion | null, existingDocs: DocumentSystem[] | undefined | null) => {
      const modifiedDocs = existingDocs?.filter(item => item.id === modifiedDoc?.id);
      modifiedDocs?.forEach(doc => {
        doc.relevantTo = modifiedDoc ? modifiedDoc.relevantTo : null;
        doc.relevantToId = modifiedDoc ? modifiedDoc.relevantToId : null;
        doc.keyWords = modifiedDoc ? modifiedDoc.keyWords : null;
      });
      return !!modifiedDocs?.length;
    },
    []
  );
  const onUpdated = useCallback(
    (modifiedDoc: Document) => {
      const result = modifiedDoc ? mapNewItemToChangedItem(modifiedDoc) : null;
      if (changedDocuments) {
        updateDocuments(result, changedDocuments);
        setDocumentsChangedCount(documentsChangedCount + 1);
      }
      if (data) {
        updateDocuments(result, data);
      }

      refreshDocuments();
      setEditItem(null);
      setIsAddModal(false);
    },
    [changedDocuments, data, updateDocuments, refreshDocuments, documentsChangedCount]
  );

  return (
    <>
      <EnhancedDetailsList<DocumentSystem, DocumentSystem, Document>
        columns={allowAdd ? columnsEdit : columnsView}
        items={data || []}
        allowAdd={allowAdd}
        addLabel="Add documents from PDC"
        columnsForAdd={columnsAdd}
        itemsToAdd={documents}
        itemsFiltered={itemsFiltered}
        changedItems={changedItems}
        mapChangedItemToItem={mapChangedDocumentToDocument}
        mapNewItemToChangedItem={mapNewItemToChangedItem}
        ownerProcessId={ownerProcessId}
        pageSize={pageSize}
        frozen={allowAdd ? false : true}
      />
      <DocumentListUpdateModal
        processId={ownerProcessId}
        isVisible={isAddModal}
        onClose={onClose}
        editItem={editItem}
        onUpdated={onUpdated}
        parents={parents}
      />
    </>
  );
};
